import { cloneElement, useRef } from 'react'
import type { ComponentType, ReactElement, Ref } from 'react'
import DraggableBase, { DraggableProps } from 'react-draggable'

// react-draggable 4.6+ ships generated types (Flow→TypeScript migration) that mark every
// Draggable prop as required at the JSX call site — an upstream regression. Its
// `DraggableProps` is a `type` alias (not an interface), so it can't be widened by
// declaration merging the way the shims in src/types/react18-library-children.d.ts are.
// Cast here to re-apply `Partial` so callers pass only the props they need.
const DraggableWithOptionalProps = DraggableBase as ComponentType<Partial<DraggableProps>>

type Props = Partial<Omit<DraggableProps, 'nodeRef' | 'children'>> & {
  // Must accept a ref to its root DOM element — that is the node that gets dragged.
  children: ReactElement
}

// React 19 removed ReactDOM.findDOMNode, which react-draggable falls back on when no
// `nodeRef` is passed: drag-start then throws and the element silently stops moving.
// The wrapper owns the nodeRef wiring so no call site can render an undraggable window.
const Draggable = ({ children, ...draggableProps }: Props) => {
  const nodeRef = useRef<HTMLElement>(null)
  return (
    <DraggableWithOptionalProps {...draggableProps} nodeRef={nodeRef}>
      {cloneElement(children as ReactElement<{ ref?: Ref<HTMLElement> }>, { ref: nodeRef })}
    </DraggableWithOptionalProps>
  )
}

export default Draggable
export type { DraggableBounds } from 'react-draggable'
